import { formatoNumero, formatoPct } from './franjas-editor.rules';
import { round2 } from './tramos-usdt-editor.rules';

/**
 * Reglas puras del modelo «distancia» de los tramos USDT (Oscar, 23/10/2026).
 * Sin Angular ni HTTP. Contrato: `docs/tramos-usdt/ESPEC-DISTANCIA.md` del API.
 *
 * Cada ruta tiene una referencia (el promedio P2P en VES, el spot en CLP), una
 * punta DESFAVORABLE para el cliente (la «chica», el tramo más chico, que es
 * la tasa publicada de hoy) y una FAVORABLE (la «grande», el tramo más
 * grande). La tabla de distancia elige, con el margen de la ruta contra la
 * referencia, cuánto de ese margen se le devuelve al cliente grande (el
 * ajuste). Lo que le queda a la casa en el tramo grande es el colchón:
 * margen − ajuste.
 *
 * Nombres de Oscar: donde la casa PAGA la moneda local (USDT→VES, USDT→CLP)
 * la peor tasa es el PISO y la mejor el TECHO; donde la COBRA (VES→USDT,
 * CLP→USDT) se invierten: TECHO = peor y PISO = mejor.
 *
 * Todo lo que el cliente ve sale del API. Lo que se calcula acá (la tasa
 * resultante de cada fila de la tabla) es solo para que el operador lea la
 * tabla, y se marca como estimado.
 */

export type RutaUsdt = 'VES-USDT' | 'USDT-VES' | 'CLP-USDT' | 'USDT-CLP';
export type FamiliaDistancia = 'VES' | 'CLP-USDT' | 'USDT-CLP';

/** Una fila de la tabla de distancia. `maxPct` null = ∞ (solo la última). */
export interface FilaDistancia {
  orden?: number;
  minPct: number | null;
  maxPct: number | null;
  ajustePct: number | null;
}

/** Campos aditivos de `GET rates/tramos-usdt/{ruta}` (todos opcionales: API vieja = ausentes). */
export interface CamposDistancia {
  modelo?: 'DISTANCIA' | 'MARGEN_MIN' | string | null;
  referencia?: number | null;
  chica?: number | null;
  grande?: number | null;
  margenPct?: number | null;
  ajustePct?: number | null;
  filaDistancia?: FilaDistancia | null;
  rangoPct?: number | null;
  precioCorte?: number | null;
  recortadoPorReferencia?: boolean | null;
  idaVueltaAlerta?: boolean | string | null;
  /** Tasa que da la tabla para el último tramo, antes de los recortes (aditivo). */
  grandeTabla?: number | null;
  /** El tramo grande quedó en el límite de ida y vuelta (aditivo). */
  recortadoPorIdaVuelta?: boolean | null;
}

/** Fila activa de una ruta (`activa.porRuta[]` del GET / previsualizar de la tabla). */
export interface ActivaRuta {
  ruta: RutaUsdt;
  margenPct: number | null;
  fila: FilaDistancia | null;
  chica: number | null;
  grande: number | null;
}

/** `GET rates/tramos-distancia/:familia` (y `previsualizar`, con errores y avisos). */
export interface TablaDistanciaResponse {
  familia: FamiliaDistancia;
  filas: FilaDistancia[];
  version: string | number | null;
  puedeEditar?: boolean;
  activa?: { modelo?: string; porRuta: ActivaRuta[] } | null;
  errores?: string[];
  avisos?: string[];
  /** Solo `previsualizar`: false = el servidor encontró errores (no se puede guardar). */
  ok?: boolean;
  /** CLP→USDT: el cambio rige desde la próxima tasa base (~2-4 min), no al instante. */
  rigeDesdeProximaBase?: boolean;
  /** Solo el PUT: 1 si cambió algo, 0 si la tabla quedó igual. */
  cambios?: number;
}

/** Un cambio de la tabla en el historial. */
export interface HistorialDistancia {
  id?: number;
  familia?: string;
  accion: string;
  antes: any;
  despues: any;
  editadoPor: string | null;
  motivo: string | null;
  createdAt: string;
}

export interface HistorialDistanciaPagina {
  items: HistorialDistancia[];
  total: number;
  pagina: number;
  tamano: number;
}

export const FILAS_MIN = 1;
export const FILAS_MAX = 30;
export const AJUSTE_DISTANCIA_MAX = 5;
export const MOTIVO_DISTANCIA_MIN = 5;
export const MOTIVO_DISTANCIA_MAX = 255;
export const HISTORIAL_DISTANCIA_TAMANO = 5;
/** MIN y MAX van de 0 a 100 % (DTO del API). */
export const MARGEN_DISTANCIA_MAX = 100;
/** Hasta 4 decimales, como las columnas DECIMAL(9,4) del API. */
export const DECIMALES_DISTANCIA = 4;

/** Tiene más de 4 decimales (el DTO del API lo rechaza con 400). */
const sobranDecimales = (x: number | null): boolean =>
  x != null && Math.abs(Math.round(x * 10 ** DECIMALES_DISTANCIA) - x * 10 ** DECIMALES_DISTANCIA) > 1e-6;

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const positivo = (v: unknown): number | null => {
  const n = num(v);
  return n != null && n > 0 ? n : null;
};

// ───────────────────────── Rutas y familias ─────────────────────────

/** Normaliza «ves-usdt», «VES_USDT» o «VES-USDT» a la ruta; null si no es una. */
export function rutaDe(v: unknown): RutaUsdt | null {
  const s = String(v ?? '').trim().toUpperCase().replace('_', '-');
  return s === 'VES-USDT' || s === 'USDT-VES' || s === 'CLP-USDT' || s === 'USDT-CLP' ? s : null;
}

/** La familia de la tabla de distancia: las dos rutas VES comparten una sola. */
export function familiaDe(ruta: RutaUsdt): FamiliaDistancia {
  return ruta === 'CLP-USDT' || ruta === 'USDT-CLP' ? ruta : 'VES';
}

/** Segmento de la URL: `ves`, `clp-usdt` o `usdt-clp`. */
export function familiaUrl(f: FamiliaDistancia): 'ves' | 'clp-usdt' | 'usdt-clp' {
  return f === 'VES' ? 'ves' : f === 'CLP-USDT' ? 'clp-usdt' : 'usdt-clp';
}

/** Rutas que usan la tabla de la familia (en el orden del panel). */
export function rutasDeFamilia(f: FamiliaDistancia): RutaUsdt[] {
  return f === 'VES' ? ['USDT-VES', 'VES-USDT'] : [f];
}

/** «USDT → VES». */
export function etiquetaRuta(ruta: RutaUsdt): string {
  return ruta.replace('-', ' → ');
}

/** «VES (USDT → VES y VES → USDT)» / «CLP → USDT». */
export function etiquetaFamilia(f: FamiliaDistancia): string {
  return f === 'VES' ? 'VES (USDT → VES y VES → USDT)' : etiquetaRuta(f);
}

/**
 * La casa PAGA la moneda local (el cliente trae USDT): USDT→VES y USDT→CLP.
 * Ahí acercarse a la referencia es SUBIR la tasa.
 */
export function casaPaga(ruta: RutaUsdt): boolean {
  return ruta.startsWith('USDT-');
}

export function esClp(ruta: RutaUsdt): boolean {
  return ruta === 'CLP-USDT' || ruta === 'USDT-CLP';
}

// ───────────────────────── Rótulos de Oscar ─────────────────────────

export interface RotulosDistancia {
  /** «PISO» o «TECHO»: la punta desfavorable (tramo chico). */
  peor: 'PISO' | 'TECHO';
  /** La punta favorable (tramo grande). */
  mejor: 'PISO' | 'TECHO';
  /** «Tasa Negociación (VES/USDT)» / «Spot USD/CLP». */
  fuente: string;
  fuenteNota: string;
  /** «margen contra el promedio P2P» / «margen contra el spot». */
  margen: string;
  /** «Comisión USDT→VES %» / «Margen de la base %». */
  comision: string;
  comisionNota: string;
  moneda: 'VES' | 'CLP';
}

export function rotulosDistancia(ruta: RutaUsdt): RotulosDistancia {
  const paga = casaPaga(ruta);
  const clp = esClp(ruta);
  return {
    peor: paga ? 'PISO' : 'TECHO',
    mejor: paga ? 'TECHO' : 'PISO',
    fuente: clp ? 'Spot USD/CLP' : 'Tasa Negociación (VES/USDT)',
    fuenteNota: clp ? 'PRECIO_SPOT · la referencia de CLP' : 'automática · promedio del P2P de Binance',
    margen: clp ? 'margen contra el spot' : 'margen contra el promedio P2P',
    comision: clp ? 'Margen de la base %' : `Comisión ${etiquetaRuta(ruta).replace(' → ', '→')} %`,
    comisionNota: clp
      ? paga
        ? 'el spot menos la última tasa de venta publicada; se mueve con el descuento base'
        : 'la última tasa de compra publicada sobre el spot; se mueve con el descuento base'
      : paga
        ? 'la casa PAGA bolívares ⇒ su peor tasa entrega menos VES que el promedio'
        : 'la casa COBRA bolívares ⇒ su peor tasa pide más VES que el promedio',
    moneda: clp ? 'CLP' : 'VES',
  };
}

// ───────────────────────── Estado de la ruta ─────────────────────────

/**
 * El API ya usa el modelo de distancia para esta ruta: dice `modelo`
 * 'DISTANCIA' y trae referencia y chica. Con la API vieja (sin campos) o con
 * `MARGEN_MIN` el panel se ve como siempre.
 */
export function esModeloDistancia(estado: CamposDistancia | null | undefined): boolean {
  return (
    String(estado?.modelo ?? '').toUpperCase() === 'DISTANCIA' &&
    positivo(estado?.referencia) != null &&
    positivo(estado?.chica) != null
  );
}

/** Normaliza una fila (acepta camelCase o snake_case). */
export function normalizarFila(raw: any, i = 0): FilaDistancia {
  const max = raw?.maxPct ?? raw?.max_pct ?? raw?.max;
  return {
    orden: num(raw?.orden) ?? i + 1,
    minPct: num(raw?.minPct ?? raw?.min_pct ?? raw?.min),
    maxPct: max == null || max === '' ? null : num(max),
    ajustePct: num(raw?.ajustePct ?? raw?.ajuste_pct ?? raw?.ajuste),
  };
}

export function normalizarFilas(raw: unknown): FilaDistancia[] {
  return Array.isArray(raw) ? raw.map((f, i) => normalizarFila(f, i)) : [];
}

/**
 * Índice de la fila activa. Manda la fila que dice el API (`filaDistancia`,
 * por orden o por mínimo); si no vino, la primera con min ≤ margen < max.
 * −1 = ninguna (margen negativo o sin fila: todos pagan la chica).
 */
export function indiceFilaActiva(
  filas: FilaDistancia[],
  margenPct: number | null | undefined,
  filaApi?: FilaDistancia | null,
): number {
  if (filaApi) {
    const f = normalizarFila(filaApi);
    const porMin = filas.findIndex(
      (x) => x.minPct != null && f.minPct != null && Math.abs(x.minPct - f.minPct) < 1e-9 &&
        (x.maxPct ?? Infinity) === (f.maxPct ?? Infinity),
    );
    if (porMin >= 0) return porMin;
    const orden = num((filaApi as any)?.orden);
    const porOrden = orden == null ? -1 : filas.findIndex((x) => x.orden === orden);
    if (porOrden >= 0) return porOrden;
  }
  const m = num(margenPct);
  if (m == null || m < 0) return -1;
  return filas.findIndex((f) => f.minPct != null && f.minPct <= m && m < (f.maxPct ?? Infinity));
}

/**
 * Tasa del tramo grande con un ajuste: la chica movida `ajuste` % hacia la
 * referencia (USDT→X sube, X→USDT baja). Redondeo half-up a 2 decimales, el
 * de los documentos. Solo para leer la tabla: la oficial la da el API.
 */
export function tasaResultante(
  ruta: RutaUsdt,
  chica: number | null | undefined,
  ajustePct: number | null | undefined,
): number | null {
  const c = positivo(chica);
  const a = num(ajustePct);
  if (c == null || a == null) return null;
  return round2(c * (1 + ((casaPaga(ruta) ? 1 : -1) * a) / 100));
}

/** Lo que le queda a la casa en el tramo grande: margen − ajuste. */
export function colchonPct(margenPct: number | null | undefined, ajustePct: number | null | undefined): number | null {
  const m = num(margenPct);
  const a = num(ajustePct);
  if (m == null) return null;
  return m - (a ?? 0);
}

/** Margen de una tasa contra la referencia, en % (positivo = la casa gana). */
export function margenContra(ruta: RutaUsdt, tasa: number | null | undefined, referencia: number | null | undefined): number | null {
  const t = positivo(tasa);
  const r = positivo(referencia);
  if (t == null || r == null) return null;
  return casaPaga(ruta) ? (1 - t / r) * 100 : (t / r - 1) * 100;
}

// ───────────────────────── Vista del bloque ─────────────────────────

export interface VistaDistancia {
  ruta: RutaUsdt;
  rotulos: RotulosDistancia;
  referencia: number;
  /** Punta desfavorable (tramo chico) = la tasa publicada. */
  peor: number;
  /** Punta favorable (tramo grande). */
  mejor: number | null;
  margenPct: number | null;
  ajustePct: number | null;
  colchonPct: number | null;
  rangoPct: number | null;
  comisionPct: number | null;
  /** «996,48 × (1 − 0,92 %) = 987,31». */
  formulaPeor: string;
  /** «el tramo devuelve 0,67 % — se acerca al promedio». */
  notaMejor: string;
  recortado: boolean;
  /** Texto del aviso de ida y vuelta; null si no hay. */
  idaVuelta: string | null;
  precioCorte: number | null;
  /** «La tabla da X, se cobra Y por …» cuando `grandeTabla` ≠ `grande`; null si no hay recorte. */
  avisoTabla: string | null;
  /**
   * La chica no es `round2(ref·(1∓c))`: la base es una fila manual o
   * desfasada. No se muestra la cuenta de la comisión.
   */
  baseReemplazada: boolean;
}

/** Texto de la punta desfavorable cuando la base no sale de la comisión. */
export const TEXTO_BASE_REEMPLAZADA = 'Tasa base publicada (fila manual o desfasada)';

/**
 * Solo VES: la chica no coincide (a 2 decimales) con referencia × (1 ∓ c):
 * la base publicada vino de una fila manual o desfasada.
 */
export function baseReemplazada(
  ruta: RutaUsdt,
  peor: number | null | undefined,
  referencia: number | null | undefined,
  comisionPct: number | null | undefined,
): boolean {
  const p = positivo(peor);
  const r = positivo(referencia);
  const c = num(comisionPct);
  if (esClp(ruta) || p == null || r == null || c == null) return false;
  const esperada = round2(r * (1 + ((casaPaga(ruta) ? -1 : 1) * c) / 100));
  return Math.abs(round2(p) - esperada) > 0.011;
}

/**
 * Aviso de recorte del tramo grande: la tabla da `grandeTabla` y se cobra
 * `grande` por la referencia o por ida y vuelta. Sin `grandeTabla` (API sin el
 * campo) o si son iguales, null.
 */
export function avisoRecorteTabla(estado: CamposDistancia | null | undefined, ruta: RutaUsdt): string | null {
  const tabla = positivo(estado?.grandeTabla);
  const cobra = positivo(estado?.grande);
  if (tabla == null || cobra == null || Math.abs(round2(tabla) - round2(cobra)) < 0.005) return null;
  const motivo = estado?.recortadoPorIdaVuelta
    ? 'ida y vuelta'
    : esClp(ruta)
      ? 'el spot (referencia)'
      : 'el promedio P2P (referencia)';
  return `La tabla da ${formatoNumero(tabla)}, se cobra ${formatoNumero(cobra)} por ${motivo}.`;
}

/**
 * Lo que pinta el bloque de Oscar para una ruta. null si el API no usa el
 * modelo de distancia (el panel queda como siempre).
 */
export function vistaDistancia(
  ruta: RutaUsdt,
  estado: CamposDistancia | null | undefined,
  comisionConfigurada?: number | null,
): VistaDistancia | null {
  if (!estado || !esModeloDistancia(estado)) return null;
  const rot = rotulosDistancia(ruta);
  const referencia = positivo(estado.referencia) as number;
  const peor = positivo(estado.chica) as number;
  const margen = num(estado.margenPct) ?? margenContra(ruta, peor, referencia);
  const ajuste = num(estado.ajustePct) ?? num(estado.filaDistancia?.ajustePct) ?? 0;
  const mejor = positivo(estado.grande) ?? tasaResultante(ruta, peor, ajuste);
  const comision = esClp(ruta) ? margen : num(comisionConfigurada) ?? margen;
  const signo = casaPaga(ruta) ? '−' : '+';
  const reemplazada = baseReemplazada(ruta, peor, referencia, esClp(ruta) ? null : num(comisionConfigurada));
  const formulaPeor = reemplazada
    ? TEXTO_BASE_REEMPLAZADA
    : esClp(ruta)
    ? casaPaga(ruta)
      ? 'la última tasa de venta publicada'
      : 'la última tasa de compra publicada'
    : `promedio × (1 ${signo} ${formatoPct(comision)}) — tu comisión`;
  const iv = estado.idaVueltaAlerta;
  return {
    ruta,
    rotulos: rot,
    referencia,
    peor,
    mejor,
    margenPct: margen,
    ajustePct: ajuste,
    colchonPct: colchonPct(margen, ajuste),
    rangoPct: num(estado.rangoPct) ?? ajuste,
    comisionPct: comision,
    formulaPeor,
    notaMejor: `el tramo devuelve ${formatoPct(ajuste)} — se ACERCA al ${esClp(ruta) ? 'spot' : 'promedio'}`,
    recortado: !!estado.recortadoPorReferencia,
    idaVuelta: iv
      ? typeof iv === 'string' && iv.trim()
        ? iv.trim()
        : 'La mejor compra quedó a menos de 0,5 % de la mejor venta: comprar y vender de vuelta casi no deja margen.'
      : null,
    precioCorte: positivo(estado.precioCorte),
    avisoTabla: avisoRecorteTabla(estado, ruta),
    baseReemplazada: reemplazada,
  };
}

/** «996,48 × (1 − 0,92 %) = 987,31» con los números de la vista (solo VES). */
export function cuentaPeor(v: VistaDistancia): string {
  if (esClp(v.ruta) || v.comisionPct == null || v.baseReemplazada) return '';
  const signo = casaPaga(v.ruta) ? '−' : '+';
  return `${v.rotulos.peor} = ${formatoNumero(v.referencia)} × (1 ${signo} ${formatoPct(v.comisionPct)}) = ${formatoNumero(v.peor)}`;
}

// ───────────────────────── Edición de la tabla ─────────────────────────

/** Copia sin referencias compartidas (y con `orden` 1…n). */
export function clonarFilas(filas: FilaDistancia[]): FilaDistancia[] {
  return filas.map((f, i) => ({ orden: i + 1, minPct: f.minPct, maxPct: f.maxPct, ajustePct: f.ajustePct }));
}

/**
 * Cambia el MAX de la fila `i` y arrastra el MIN de la siguiente (las filas
 * son contiguas). La última no tiene MAX (∞).
 */
export function cambiarMax(filas: FilaDistancia[], i: number, valor: unknown): FilaDistancia[] {
  const out = clonarFilas(filas);
  if (i < 0 || i >= out.length - 1) return out;
  const v = num(valor);
  out[i].maxPct = v;
  out[i + 1].minPct = v;
  return out;
}

export function cambiarAjusteFila(filas: FilaDistancia[], i: number, valor: unknown): FilaDistancia[] {
  const out = clonarFilas(filas);
  if (out[i]) out[i].ajustePct = num(valor);
  return out;
}

/**
 * Agrega una fila al final: la última (que llegaba a ∞) pasa a terminar en su
 * MIN + 0,1 y la nueva arranca ahí hasta ∞, con el mismo ajuste.
 */
export function agregarFila(filas: FilaDistancia[]): FilaDistancia[] {
  const out = clonarFilas(filas);
  if (out.length >= FILAS_MAX) return out;
  if (!out.length) return [{ orden: 1, minPct: 0, maxPct: null, ajustePct: 0 }];
  const ult = out[out.length - 1];
  const corte = Math.round(((ult.minPct ?? 0) + 0.1) * 10000) / 10000;
  ult.maxPct = corte;
  out.push({ orden: out.length + 1, minPct: corte, maxPct: null, ajustePct: ult.ajustePct ?? 0 });
  return out;
}

/**
 * Quita la fila `i` sin dejar huecos: su rango lo toma la anterior (o, si es
 * la primera, la siguiente arranca en 0). Tiene que quedar al menos una.
 */
export function quitarFila(filas: FilaDistancia[], i: number): FilaDistancia[] {
  const out = clonarFilas(filas);
  if (out.length <= FILAS_MIN || i < 0 || i >= out.length) return out;
  const quitada = out[i];
  out.splice(i, 1);
  if (i === 0) out[0].minPct = 0;
  else out[i - 1].maxPct = quitada.maxPct;
  return clonarFilas(out);
}

/** Lo que viaja al API: números, con `max` null solo en la última. */
export function filasParaEnviar(filas: FilaDistancia[]): { orden: number; minPct: number; maxPct: number | null; ajustePct: number }[] {
  return filas.map((f, i) => ({
    orden: i + 1,
    minPct: Number(f.minPct),
    maxPct: i === filas.length - 1 ? null : Number(f.maxPct),
    ajustePct: Number(f.ajustePct),
  }));
}

/** Mismas reglas que valida el API (las que bloquean guardar). */
export function erroresFilas(filas: FilaDistancia[]): string[] {
  const e: string[] = [];
  if (filas.length < FILAS_MIN) return ['Tiene que haber al menos una fila.'];
  if (filas.length > FILAS_MAX) e.push(`Máximo ${FILAS_MAX} filas.`);
  filas.forEach((f, i) => {
    const n = i + 1;
    const ultima = i === filas.length - 1;
    if (f.minPct == null) e.push(`Fila ${n}: falta el MIN.`);
    if (!ultima && f.maxPct == null) e.push(`Fila ${n}: solo la última puede llegar a ∞.`);
    if (f.minPct != null && f.maxPct != null && !ultima && !(f.minPct < f.maxPct)) {
      e.push(`Fila ${n}: el MIN tiene que ser menor que el MAX.`);
    }
    if (f.ajustePct == null || f.ajustePct < 0 || f.ajustePct > AJUSTE_DISTANCIA_MAX) {
      e.push(`Fila ${n}: el ajuste tiene que ser de 0 a ${AJUSTE_DISTANCIA_MAX} %.`);
    }
    if ((f.minPct != null && (f.minPct < 0 || f.minPct > MARGEN_DISTANCIA_MAX)) || (f.maxPct != null && (f.maxPct < 0 || f.maxPct > MARGEN_DISTANCIA_MAX))) {
      e.push(`Fila ${n}: el MIN y el MAX van de 0 a ${MARGEN_DISTANCIA_MAX} %.`);
    }
    if (sobranDecimales(f.minPct) || sobranDecimales(f.maxPct) || sobranDecimales(f.ajustePct)) {
      e.push(`Fila ${n}: hasta ${DECIMALES_DISTANCIA} decimales.`);
    }
    if (i > 0 && filas[i - 1].maxPct != null && f.minPct != null && Math.abs(Number(filas[i - 1].maxPct) - f.minPct) > 1e-9) {
      e.push(`Fila ${n}: tiene que empezar donde termina la anterior.`);
    }
  });
  if (filas[0]?.minPct != null && filas[0].minPct !== 0) e.push('La primera fila tiene que empezar en 0.');
  return e;
}

/** Avisos que no bloquean (los mismos que manda el API). */
export function avisosFilas(filas: FilaDistancia[]): string[] {
  const a: string[] = [];
  filas.forEach((f, i) => {
    const n = i + 1;
    if (i > 0 && f.ajustePct != null && filas[i - 1].ajustePct != null && f.ajustePct < Number(filas[i - 1].ajustePct)) {
      a.push(`Fila ${n}: el ajuste baja aunque el margen sube.`);
    }
    // Como el API (`validarFilasDistancia`): también la primera fila (MIN 0) con ajuste > 0.
    if (f.ajustePct != null && f.minPct != null && f.ajustePct > 0 && f.ajustePct >= f.minPct) {
      a.push(`Fila ${n}: el ajuste (${formatoPct(f.ajustePct)}) alcanza el MIN de la fila: el tramo grande cruzaría la referencia.`);
    }
  });
  return a;
}

export function hayCambiosFilas(a: FilaDistancia[] | null, b: FilaDistancia[] | null): boolean {
  if (!a || !b) return false;
  return JSON.stringify(filasParaEnviar(a)) !== JSON.stringify(filasParaEnviar(b));
}

export function motivoDistanciaValido(m: string | null | undefined): boolean {
  const t = (m ?? '').trim().length;
  return t >= MOTIVO_DISTANCIA_MIN && t <= MOTIVO_DISTANCIA_MAX;
}

/** «0 – 0,3» / «0,9 – ∞» para el historial y los textos. */
export function rangoFila(f: FilaDistancia): string {
  const fmt = (x: number | null) => (x == null ? '∞' : Number(x).toLocaleString('es-CL', { maximumFractionDigits: 4 }));
  return `${fmt(f.minPct)} – ${fmt(f.maxPct)}`;
}

/** Antes y después de un cambio del historial, por separado (el API guarda los dos). */
export function antesDespuesHistorial(h: HistorialDistancia | null | undefined): { antes: string; despues: string } {
  const txt = (raw: any) => {
    const f = normalizarFilas(raw?.filas ?? raw);
    return f.length ? f.map((x) => `${rangoFila(x)}: ${formatoPct(x.ajustePct)}`).join(' · ') : '—';
  };
  return { antes: txt(h?.antes), despues: txt(h?.despues) };
}

/** Resumen de un cambio del historial: «6 filas → 7 filas». */
export function resumenHistorialDistancia(h: HistorialDistancia | null | undefined): string {
  if (!h) return '';
  const antes = normalizarFilas(h.antes?.filas ?? h.antes);
  const despues = normalizarFilas(h.despues?.filas ?? h.despues);
  const txt = (f: FilaDistancia[]) => f.map((x) => `${rangoFila(x)}: ${formatoPct(x.ajustePct)}`).join(' · ');
  if (!antes.length) return despues.length ? txt(despues) : '';
  return `${antes.length} ${antes.length === 1 ? 'fila' : 'filas'} → ${despues.length} ${despues.length === 1 ? 'fila' : 'filas'}: ${txt(despues)}`;
}

/** Normaliza `activa.porRuta` (rutas en mayúscula, números o null). */
export function normalizarActiva(r: TablaDistanciaResponse | null | undefined): ActivaRuta[] {
  const lista = r?.activa?.porRuta;
  if (!Array.isArray(lista)) return [];
  return lista
    .map((x: any) => ({
      ruta: rutaDe(x?.ruta) as RutaUsdt,
      margenPct: num(x?.margenPct),
      fila:
        x?.fila && typeof x.fila === 'object'
          ? normalizarFila(x.fila)
          : num(x?.fila) != null
            ? { orden: num(x.fila) as number, minPct: null, maxPct: null, ajustePct: null }
            : null,
      chica: positivo(x?.chica),
      grande: positivo(x?.grande),
    }))
    .filter((x) => !!x.ruta);
}

// ───────────────────────── Tramos por monto con el modelo de distancia ─────────────────────────

/** Tasa estimada de un tramo por monto (misma forma que `TasaLocal` del editor de tramos). */
export interface TasaTramoDistancia {
  tasa: number;
  tasaPropia: number;
  calculada: number;
  /** Lo que le queda a la casa sobre la referencia, en %. */
  margenPct: number;
  /** Cuánto mejora la chica para el cliente, en %. */
  mejoraPct: number;
  recortado: boolean;
}

/**
 * Tasa de cada tramo por monto con el modelo de distancia, para el editor
 * (la oficial la da la vista previa del API). AUTO reparte el ajuste parejo:
 * tramo i = chica · (1 ± a·i/(n−1)/100); PCT = mejora % manual sobre la chica.
 * Ninguno cruza la referencia y un tramo más grande nunca es peor que el
 * anterior. Vacío si faltan insumos.
 */
export function calcularTasasDistancia(
  ruta: RutaUsdt,
  estado: CamposDistancia | null | undefined,
  tramos: { modo: 'AUTO' | 'PCT' | string; ajuste: number | null }[],
  base?: number | null,
): TasaTramoDistancia[] {
  const ref = positivo(estado?.referencia);
  const chica = positivo(estado?.chica) ?? positivo(base);
  const n = tramos.length;
  if (ref == null || chica == null || !n) return [];
  // Como el API: AUTO reparte el `rangoPct` (|grande − chica| / chica); sin él, el ajuste.
  const a = num(estado?.rangoPct) ?? num(estado?.ajustePct) ?? num(estado?.filaDistancia?.ajustePct) ?? 0;
  const paga = casaPaga(ruta);
  const signo = paga ? 1 : -1;
  // Tope: la referencia (o la chica, si ya está del otro lado de la referencia).
  const tope = paga ? Math.max(ref, chica) : Math.min(ref, chica);
  let anterior: number | null = null;
  return tramos.map((t, i) => {
    // PCT sobre el 5 % se recorta al 5 %, como el API.
    const pedido = t.modo === 'PCT' ? Math.min(Number(t.ajuste) || 0, AJUSTE_DISTANCIA_MAX) : n === 1 ? 0 : (a * i) / (n - 1);
    const calculada = i === 0 && t.modo !== 'PCT' ? round2(chica) : round2(chica * (1 + (signo * pedido) / 100));
    const propia = paga ? Math.min(calculada, tope) : Math.max(calculada, tope);
    // Monótono: a mayor monto, igual o mejor para el cliente.
    const tasa = anterior == null ? propia : paga ? Math.max(propia, anterior) : Math.min(propia, anterior);
    anterior = tasa;
    return {
      tasa,
      tasaPropia: propia,
      calculada,
      margenPct: margenContra(ruta, tasa, ref) as number,
      mejoraPct: paga ? (tasa / chica - 1) * 100 : (1 - tasa / chica) * 100,
      recortado: Math.abs(tasa - calculada) > 0.011,
    };
  });
}

/** Divisor para pasar un monto en moneda local a USDT al elegir tramo: `precioCorte` o, si no vino, la base. */
export function divisorTramo(estado: CamposDistancia | null | undefined, base: number | null | undefined): number | null {
  return positivo(estado?.precioCorte) ?? positivo(base);
}
